/**
 * مستفيد الـAPI — نقطة الاتصال الوحيدة بالخادم.
 * - رمز الوصول في الذاكرة، ورمز التحديث في localStorage (تدوير متدوّر يُبطل العائلة عند إعادة الاستخدام).
 * - 401 يُحاول تدويراً واحداً ثم يُخرج المستخدم؛ لا حلقات إعادة محاولة.
 * - رأس X-Institution-Id يُرسَل تلقائياً متى وُجد سياق مؤسسة.
 * - أخطاء 422/403 من الخادم تصل عربية جاهزة — تُعرَض كما هي ولا تُعاد صياغتها.
 */
import type { TokenPair } from './types';
import { noteResponse } from './service-health';
import { SESSION_END_TEXT, idleSeconds, sessionInfo, signoutReason } from './session';

const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';
/** قاعدةُ الـAPI — لمصدر <video> المحمي بثّاً (0124) حيث لا ترويسةَ تُرسل */
export const API_BASE = BASE;
const REFRESH_KEY = 'rafrafa.refreshToken';
const INSTITUTION_KEY = 'rafrafa.institutionId';
/**
 * رمز الوصول يُخبَّأ في تخزين الجلسة (تبويب واحد، يُمسح بإغلاقه) لا في localStorage.
 * السبب ليس الراحة: بدونه كل إعادة تحميل صفحة تُدوّر رمز التحديث، وتبويبان يُحدَّثان معاً
 * قد يتسابقان على التدوير فيُكتشف «إعادة استخدام» وتُبطَل العائلة ويخرج المستخدم بلا سبب.
 * لا يزيد هذا سطح الهجوم: رمز التحديث نفسه في تخزين المتصفح أصلاً.
 */
const ACCESS_KEY = 'rafrafa.accessToken';

function readAccess(): string | null {
  try { return sessionStorage.getItem(ACCESS_KEY); } catch { return null; }
}

let accessToken: string | null = readAccess();
let refreshing: Promise<string | null> | null = null;
const listeners = new Set<(reason: 'signed-out' | 'view-as-ended') => void>();

export function onAuthLost(fn: (reason: 'signed-out' | 'view-as-ended') => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const tokens = {
  get access() { return accessToken; },
  get refresh(): string | null {
    try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
  },
  set(pair: Pick<TokenPair, 'accessToken' | 'refreshToken' | 'session'>) {
    accessToken = pair.accessToken;
    sessionInfo.set(pair.session);
    // دخولٌ أو تدوير = تواصلٌ حديثٌ مع الخادم؛ يُعدّ منه النبضُ التالي
    try { localStorage.setItem(ROTATED_KEY, String(Date.now())); } catch { /* تجاهل */ }
    try { localStorage.setItem(REFRESH_KEY, pair.refreshToken); } catch { /* وضع خاص أو تخزين معطّل */ }
    try { sessionStorage.setItem(ACCESS_KEY, pair.accessToken); } catch { /* تجاهل */ }
  },
  clear() {
    accessToken = null;
    sessionInfo.clear();
    try { localStorage.removeItem(REFRESH_KEY); localStorage.removeItem(INSTITUTION_KEY); } catch { /* تجاهل */ }
    try { sessionStorage.removeItem(ACCESS_KEY); } catch { /* تجاهل */ }
  },
};

/**
 * جلسةُ إدارة المنصة المحفوظة أثناء الدخول إلى حسابٍ تجريبي (21 سبتمبر 2026).
 *
 * المديرُ يدخل الحسابَ التجريبيّ بجلسةٍ جديدة، ورمزُ تحديث جلسته هو يُحفظ هنا
 * جانباً — في التخزين نفسِه الذي يحفظ رمزَ التحديث أصلاً، فلا سطحَ جديد — ثم
 * يعود به بتدويرٍ عادي. ولا يُحفظ رمزُ وصولٍ ولا كلمةُ مرور.
 */
const RETURN_KEY = 'rafrafa.sandbox.returnRefresh';
const RETURN_INST_KEY = 'rafrafa.sandbox.returnInstitution';
const SANDBOX_AS_KEY = 'rafrafa.sandbox.as';
export const sandboxReturn = {
  get(): { refresh: string; institutionId: string | null; as: string } | null {
    try {
      const refresh = localStorage.getItem(RETURN_KEY);
      return refresh ? { refresh, institutionId: localStorage.getItem(RETURN_INST_KEY), as: localStorage.getItem(SANDBOX_AS_KEY) ?? '' } : null;
    } catch { return null; }
  },
  set(v: { refresh: string; institutionId: string | null; as: string }) {
    try {
      localStorage.setItem(RETURN_KEY, v.refresh);
      if (v.institutionId) localStorage.setItem(RETURN_INST_KEY, v.institutionId); else localStorage.removeItem(RETURN_INST_KEY);
      localStorage.setItem(SANDBOX_AS_KEY, v.as);
    } catch { /* تجاهل */ }
  },
  clear() {
    try { localStorage.removeItem(RETURN_KEY); localStorage.removeItem(RETURN_INST_KEY); localStorage.removeItem(SANDBOX_AS_KEY); } catch { /* تجاهل */ }
  },
};

/**
 * زيارةٌ بكود دخولٍ تجريبي (0091): زائرٌ من خارج المنصة دخل حساباً تجريبياً من `/try` — لا جلسةَ إدارةٍ يعود إليها. يُحفظ وصفُ الكود
 * واسمُه ونهايةُ المدّة فيقولها الشريطُ أعلى كل صفحة. والخادمُ هو من يُغلق الجلسةَ عند النهاية؛ هذا عرضٌ لا حارس.
 */
const VISIT_KEY = 'rafrafa.sandbox.visit';
export interface SandboxVisit { label: string; until: string; visitorName: string; as: string }
const VIEWAS_KEY = 'rafrafa.viewAs';
export interface ViewAsState { returnRefresh: string | null; institutionId: string | null; memberId: string; memberName: string; until: string }
/** «عرضٌ بعين العضو» (0122): جلسةُ المدير محفوظةٌ جانباً، ورمزُ العرض في الذاكرة بلا تحديث؛ الكتابةُ تُردّ هنا قبل الخادم (وهو الحَكَم) */
export const viewAs = {
  get(): ViewAsState | null {
    try { const raw = localStorage.getItem(VIEWAS_KEY); const v = raw ? (JSON.parse(raw) as ViewAsState) : null; return v && Date.parse(v.until) > Date.now() ? v : null; } catch { return null; }
  },
  set(v: ViewAsState) { try { localStorage.setItem(VIEWAS_KEY, JSON.stringify(v)); } catch { /* تجاهل */ } },
  clear() { try { localStorage.removeItem(VIEWAS_KEY); } catch { /* تجاهل */ } },
};
export const VIEW_AS_END_PATH = '/staff/view-as/end';

export const sandboxVisit = {
  get(): SandboxVisit | null {
    try { const raw = localStorage.getItem(VISIT_KEY); const v = raw ? (JSON.parse(raw) as SandboxVisit) : null; return v && Date.parse(v.until) > Date.now() ? v : null; } catch { return null; }
  },
  set(v: SandboxVisit) { try { localStorage.setItem(VISIT_KEY, JSON.stringify(v)); } catch { /* تجاهل */ } },
  clear() { try { localStorage.removeItem(VISIT_KEY); } catch { /* تجاهل */ } },
};

export const institutionScope = {
  get(): string | null {
    try { return localStorage.getItem(INSTITUTION_KEY); } catch { return null; }
  },
  set(id: string | null) {
    try {
      if (id) localStorage.setItem(INSTITUTION_KEY, id);
      else localStorage.removeItem(INSTITUTION_KEY);
    } catch { /* تجاهل */ }
  },
};

/** خطأ الـAPI — يحمل الكود المرجعي (BR-… أو PERMISSION_DENIED) والرسالة العربية كما جاءت */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** هل الخطأ مخالفة قاعدة عمل موثَّقة (يُعرَض داخل النموذج) أم عطل عام (يُعرَض كتنبيه)؟ */
  get isBusinessRule(): boolean { return this.status === 422 || this.code.startsWith('BR-'); }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** تجاوز سياق المؤسسة المحفوظ (لمسارات المنصة العابرة للمؤسسات) */
  institutionId?: string | null;
  /** مسار عام لا يحمل رمز وصول */
  anonymous?: boolean;
  signal?: AbortSignal;
}

const ROTATED_KEY = 'rafrafa.lastRotation';
function lastRotation(): number {
  try { return Number(localStorage.getItem(ROTATED_KEY)) || 0; } catch { return 0; }
}

/**
 * التدويرُ بقفلٍ مشترك بين التبويبات (Web Locks): تبويبان يدوّران رمزَ التحديث نفسَه معاً يبدوان
 * للخادم «إعادةَ استعمال» فيُبطل الجلسة كلَّها. بالقفل يدوّر واحدٌ، والثاني يقرأ الرمزَ الجديد بعده.
 */
async function rotate(): Promise<string | null> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? (locks.request('rafrafa-rotate', () => rotateNow()) as Promise<string | null>) : rotateNow();
}

/**
 * نبضُ النشاط (0054): ما دام صاحبُ الجلسة ينقر ويكتب، يُدوَّر الرمزُ كل عشر دقائق ولو لم تطلب الصفحةُ
 * شيئاً — فيعرف الخادمُ أنه حاضر. ومن قرأ صفحةً طويلةً ساعةً يمرّر فيها لا يُطرد عند أول نقرة.
 */
export async function keepAlive(): Promise<void> {
  if (!tokens.refresh || Date.now() - lastRotation() < 10 * 60_000) return;
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  const run = async () => {
    // تبويبٌ آخر نبض أثناء انتظار القفل — لا حاجة لثانٍ
    if (Date.now() - lastRotation() < 10 * 60_000) return;
    let fresh: string | null;
    try { fresh = await rotateNow(); } catch (e) { if (e instanceof RotateTransient) return; throw e; }
    if (!fresh) { tokens.clear(); listeners.forEach((l) => l('signed-out')); }
  };
  await (locks ? locks.request('rafrafa-rotate', run) : run());
}

/**
 * فشلٌ عابر في التدوير — لا شبكة، أو الخادمُ يُعاد تشغيله (5xx)، أو حدُّ المحاولات (429). **لا يُخرج أحداً** (26 أكتوبر 2026):
 * كان كلُّ جوابٍ غير ناجح يُقرأ «انتهت الجلسة»، فيخرج من فتح جوالَه في مصعدٍ أو أثناء نشر نسخةٍ جديدة.
 */
class RotateTransient extends Error {}

/**
 * مفتاحُ طلب التدوير (0116): يُحفظ قبل الإرسال ويبقى حتى يصل الجواب. الجوالُ يجمّد الصفحةَ حين ينتقل صاحبُها إلى
 * تطبيقٍ آخر (ينسخ رابطَ الضيف إلى واتساب مثلاً)؛ فإن وصل الطلبُ وضاع جوابُه عاد المتصفّحُ بالرمز القديم **وبالمفتاح نفسِه**
 * فيعرف الخادمُ أنها إعادةٌ لا سرقة. المفتاحُ مربوطٌ بالرمز الذي أُرسل معه — رمزٌ جديد يأخذ مفتاحاً جديداً.
 */
const RETRY_KEY = 'rafrafa.rotateRetry';
function retryKeyFor(rt: string): string {
  const tag = rt.slice(-24);
  try {
    const saved = JSON.parse(localStorage.getItem(RETRY_KEY) ?? 'null') as { tag: string; key: string } | null;
    if (saved?.tag === tag) return saved.key;
  } catch { /* تخزينٌ معطّل أو قيمةٌ تالفة: مفتاحٌ جديد */ }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const key = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  try { localStorage.setItem(RETRY_KEY, JSON.stringify({ tag, key })); } catch { /* تجاهل */ }
  return key;
}

async function rotateNow(): Promise<string | null> {
  const rt = tokens.refresh;
  if (!rt) return null;
  let res: Response;
  try {
    // كم مضى على آخر نقرةٍ أو كتابة في أي تبويب — بها يحكم الخادمُ على الخمول (0054)
    res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt, idleSeconds: idleSeconds(), retryKey: retryKeyFor(rt) }),
    });
  } catch {
    throw new RotateTransient();
  }
  if (res.status === 429 || res.status >= 500) throw new RotateTransient();
  if (!res.ok) {
    // انتهاءُ الجلسة يُقال بسببه في صفحة الدخول — لا خروجٌ صامت يظنّه صاحبُه عطلاً
    try {
      const body = (await res.json()) as { code?: string; message?: { code?: string } };
      const code = body.code ?? body.message?.code;
      if (code && SESSION_END_TEXT[code]) signoutReason.set(SESSION_END_TEXT[code]);
    } catch { /* بلا جسم */ }
    return null;
  }
  let pair: TokenPair;
  try { pair = (await res.json()) as TokenPair; } catch { throw new RotateTransient(); }
  tokens.set(pair);
  try { localStorage.removeItem(RETRY_KEY); } catch { /* تجاهل */ }
  return pair.accessToken;
}

/** تدوير واحد مشترك بين كل الطلبات المتزامنة — لا تدويرات متوازية تُبطل بعضها */
function rotateOnce(): Promise<string | null> {
  refreshing ??= rotate().finally(() => { refreshing = null; });
  return refreshing;
}

/** التدويرُ لطلبٍ جارٍ: الفشلُ العابر خطأُ شبكةٍ يُعاد بعده — لا خروج؛ و`null` وحده (رفضُ الخادم) يُخرج */
async function rotateForRequest(): Promise<string | null> {
  try { return await rotateOnce(); } catch (e) {
    if (e instanceof RotateTransient) throw new ApiError(0, 'NETWORK', 'تعذّر الوصول إلى الخادم. تأكد من اتصالك ثم أعد المحاولة — جلستك باقية.');
    throw e;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  let code = `HTTP_${res.status}`;
  let message = 'تعذّر إتمام الطلب — حاول مرة أخرى.';
  let details: unknown;
  try {
    const body = (await res.json()) as Record<string, unknown>;
    const inner = (body.message ?? body) as Record<string, unknown> | string;
    if (typeof inner === 'object' && inner !== null) {
      if (typeof inner.code === 'string') code = inner.code;
      if (typeof inner.message === 'string') message = inner.message;
      details = inner.details;
    } else if (typeof inner === 'string') {
      message = inner;
    }
    if (typeof body.code === 'string') code = body.code;
    // قواعدُ العمل تضع `details` في الجذر لا داخل الرسالة (20 أكتوبر 2026) — بها تعرف الواجهةُ ما تقترحه كخطوةٍ تالية
    if (details === undefined && body.details !== undefined) details = body.details;
    if (Array.isArray(body.message)) message = body.message.join(' · ');
  } catch { /* استجابة بلا جسم JSON */ }
  if (res.status === 403 && code === `HTTP_403`) {
    message = 'لا تملك صلاحية هذا الإجراء في هذه المؤسسة — سُجِّلت المحاولة في سجل التدقيق.';
  }
  // حدُّ المحاولات (Throttler) يردّ نصّاً إنجليزياً — والناسُ يرونه تحت زرّ «أنشئ حسابي» مثلاً
  if (res.status === 429) {
    message = 'محاولاتٌ كثيرة في وقتٍ قصير — انتظر دقيقةً ثم أعد المحاولة.';
  }
  if (res.status === 0 || res.status >= 500) {
    message = 'الخادم لا يستجيب حالياً. لم يُحفظ أي تغيير — أعد المحاولة بعد قليل.';
  }
  return new ApiError(res.status, code, message, details);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, signal } = opts;
  const institutionId = opts.institutionId === undefined ? institutionScope.get() : opts.institutionId;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    if (institutionId) headers['X-Institution-Id'] = institutionId;
    return fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  // عرضٌ بعين العضو (0122): للقراءة وحدها — أيُّ كتابةٍ تُردّ هنا بالرسالة نفسِها التي يردّ بها الخادم، إلا الخروجَ من العرض
  if (!anonymous && viewAs.get() && method !== 'GET' && path !== VIEW_AS_END_PATH) {
    throw new ApiError(403, 'VIEWAS-001', 'للقراءة فقط — أنت تعرض شاشاتِ العضو ولا تكتب باسمه.');
  }
  // بعد إعادة تحميل الصفحة يضيع رمز الوصول من الذاكرة ويبقى رمز التحديث محفوظاً:
  // ندوّر أولاً بدل إطلاق طلب محكوم عليه بـ401 ثم إعادته.
  if (!anonymous && !accessToken && tokens.refresh) await rotateForRequest();

  let res: Response;
  try {
    res = await send(anonymous ? null : accessToken);
  } catch (e) {
    if (!(e instanceof DOMException && e.name === 'AbortError')) noteResponse(0, method);
    throw new ApiError(0, 'NETWORK', 'تعذّر الوصول إلى الخادم. تأكد من تشغيله ثم أعد المحاولة.');
  }
  noteResponse(res.status, method);

  if (res.status === 401 && !anonymous && viewAs.get()) {
    // رمزُ العرض لا يُدوَّر: انتهى أو أُنهي — فالعودةُ إلى جلسة المدير
    listeners.forEach((l) => l('view-as-ended'));
    throw new ApiError(401, 'VIEWAS-ENDED', 'انتهى العرضُ بعين العضو — عدتَ إلى حسابك.');
  }
  if (res.status === 401 && !anonymous) {
    const fresh = await rotateForRequest();
    if (fresh) {
      res = await send(fresh);
    } else {
      tokens.clear();
      listeners.forEach((l) => l('signed-out'));
      throw new ApiError(401, 'SESSION_EXPIRED', 'انتهت الجلسة — سجّل الدخول من جديد.');
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * رفع ملف (multipart). لا نضع Content-Type بأنفسنا — المتصفّح يضيفه مع الحدّ الفاصل.
 * يمرّ بنفس دورة التدوير والأخطاء التي يمرّ بها `request`، فرسائل FILE-00x
 * وGALLERY-001 وPHOTO-001 تصل عربيةً جاهزة بكودها.
 */
/**
 * رفعٌ يُبلغ عن تقدّمه (28 أكتوبر 2026 — فيديو مرفقٍ بمئات الميغابايت يأخذ دقائق على الجوال، وزرٌّ يدور وحده يبدو عالقاً):
 * `fetch` لا يُخبر بتقدّم الرفع، فيُرسَل بـXMLHttpRequest ويُعاد جوابُه `Response` فتمضي بعده الدورةُ نفسُها (401 والأخطاء).
 */
function xhrPost(url: string, headers: Record<string, string>, body: FormData, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      const noBody = xhr.status === 204 || xhr.status === 205 || xhr.status === 304;
      resolve(new Response(noBody ? null : xhr.responseText, { status: xhr.status, headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') ?? 'application/json' } }));
    };
    xhr.onerror = () => reject(new TypeError('Network request failed'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

export async function upload<T>(
  path: string,
  file: File,
  fields: Record<string, string | number | boolean | undefined> = {},
  opts: Omit<RequestOptions, 'method' | 'body'> & { field?: string; onProgress?: (fraction: number) => void } = {},
): Promise<T> {
  const { field = 'file', anonymous = false, signal, onProgress } = opts;
  const institutionId = opts.institutionId === undefined ? institutionScope.get() : opts.institutionId;

  const send = async (token: string | null): Promise<Response> => {
    const body = new FormData();
    body.append(field, file);
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') body.append(k, String(v));
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (institutionId) headers['X-Institution-Id'] = institutionId;
    return onProgress ? xhrPost(`${BASE}${path}`, headers, body, onProgress, signal) : fetch(`${BASE}${path}`, { method: 'POST', headers, body, signal });
  };

  if (!anonymous && !accessToken && tokens.refresh) await rotateForRequest();

  let res: Response;
  try {
    res = await send(anonymous ? null : accessToken);
  } catch {
    noteResponse(0, 'POST');
    throw new ApiError(0, 'NETWORK', 'تعذّر الوصول إلى الخادم. تأكد من تشغيله ثم أعد المحاولة.');
  }
  noteResponse(res.status, 'POST');
  if (res.status === 401 && !anonymous) {
    const fresh = await rotateForRequest();
    if (fresh) res = await send(fresh);
    else {
      tokens.clear();
      listeners.forEach((l) => l('signed-out'));
      throw new ApiError(401, 'SESSION_EXPIRED', 'انتهت الجلسة — سجّل الدخول من جديد.');
    }
  }
  if (!res.ok) throw await parseError(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * رفع أكثر من ملفٍ في طلبٍ واحد — نموذج «التسجيل الأكاديمي» يرفع الرخصة والهوية معاً،
 * والخادم يستقبلهما بحقلين مسمّيين (`license` و`identity`) لا بحقلٍ مكرَّر.
 * ما عدا ذلك فهو `upload` نفسُه: بلا `Content-Type` يدوي، وبنفس دورة التدوير والأخطاء.
 */
export async function uploadFields<T>(
  path: string,
  files: Record<string, File | undefined>,
  fields: Record<string, string | number | boolean | undefined> = {},
  opts: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<T> {
  const { anonymous = false, signal } = opts;
  const institutionId = opts.institutionId === undefined ? institutionScope.get() : opts.institutionId;

  const send = async (token: string | null): Promise<Response> => {
    const body = new FormData();
    for (const [k, f] of Object.entries(files)) if (f) body.append(k, f);
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') body.append(k, String(v));
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (institutionId) headers['X-Institution-Id'] = institutionId;
    return fetch(`${BASE}${path}`, { method: 'POST', headers, body, signal });
  };

  if (!anonymous && !accessToken && tokens.refresh) await rotateForRequest();

  let res: Response;
  try {
    res = await send(anonymous ? null : accessToken);
  } catch {
    noteResponse(0, 'POST');
    throw new ApiError(0, 'NETWORK', 'تعذّر الوصول إلى الخادم. تأكد من تشغيله ثم أعد المحاولة.');
  }
  noteResponse(res.status, 'POST');
  if (res.status === 401 && !anonymous) {
    const fresh = await rotateForRequest();
    if (fresh) res = await send(fresh);
    else {
      tokens.clear();
      listeners.forEach((l) => l('signed-out'));
      throw new ApiError(401, 'SESSION_EXPIRED', 'انتهت الجلسة — سجّل الدخول من جديد.');
    }
  }
  if (!res.ok) throw await parseError(res);
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * بايتات ملفٍّ محميّ — **تُعاد Blob لا رابطَ كائن** (18 سبتمبر 2026).
 *
 * كانت تعيد `URL.createObjectURL(...)`، فيُخزَّن **نصُّ الرابط** في ذاكرة
 * الاستعلامات ويُبطِله المكوّن عند تفريغه. فإذا عاد المستخدم إلى الصفحة (تبديلُ
 * تبويبٍ يكفي) سلّمته الذاكرةُ رابطاً مُبطَلاً، فتُكسر الصورة ولا تعود حتى يُعاد
 * تحميل الصفحة كلَّها — وهذا بالضبط ما كان يُشتكى منه.
 *
 * الـBlob لا يُبطَل ولا يملك دورةَ حياة، فتخزينُه آمن؛ ورابطُ الكائن يصنعه كلُّ
 * مكوّنٍ لنفسه ويُبطله عند تفريغه هو. القاعدة: **لا يُخزَّن في ذاكرة الاستعلامات
 * ما يملك دورةَ حياةٍ يُبطلها غيرُ من خزّنه.**
 */
export async function fetchBlob(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Blob> {
  const { anonymous = false, signal } = opts;
  const institutionId = opts.institutionId === undefined ? institutionScope.get() : opts.institutionId;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (institutionId) headers['X-Institution-Id'] = institutionId;
    return fetch(`${BASE}${path}`, { headers, signal });
  };

  if (!anonymous && !accessToken && tokens.refresh) await rotateForRequest();

  let res: Response;
  try {
    res = await send(anonymous ? null : accessToken);
  } catch {
    noteResponse(0, 'GET');
    throw new ApiError(0, 'NETWORK', 'تعذّر الوصول إلى الخادم. تأكد من تشغيله ثم أعد المحاولة.');
  }
  noteResponse(res.status, 'GET');
  if (res.status === 401 && !anonymous) {
    const fresh = await rotateForRequest();
    if (fresh) res = await send(fresh);
  }
  if (!res.ok) throw await parseError(res);
  return res.blob();
}

/**
 * رابطُ كائنٍ لمرةٍ واحدة — للتنزيل وحده (`<a download>` ثم `revoke`).
 * لا يُستعمل في عرضٍ مستمرّ ولا يُخزَّن في ذاكرة الاستعلامات: انظر `fetchBlob`.
 */
export async function fetchBlobUrl(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<string> {
  return URL.createObjectURL(await fetchBlob(path, opts));
}

export const api = {
  get: <T>(path: string, o?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...o, method: 'GET' }),
  post: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...o, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...o, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...o, method: 'PUT', body }),
  del: <T>(path: string, o?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...o, method: 'DELETE' }),
  upload,
  uploadFields,
  blob: fetchBlob,
  blobUrl: fetchBlobUrl,
};

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}
